// Read every row a supabase-js query matches.
//
// PostgREST on this project caps one response at 1,000 rows and returns no
// error. A query without a cursor therefore looks successful while dropping
// everything past the cap — the Expenses page showed "Total invoices 1000"
// with 1,854 rows in pending_invoices, and invoice OAZO9FN1-0005 (row 1,032
// by created_at) could not be searched.
//
// Walk `key` upward until a page comes back EMPTY. Stopping on a short page
// is the same bug again whenever the server cap is below pageSize. Not
// OFFSET: a row inserted or deleted mid-read shifts an offset window.
// `build` must return a NEW query each call (filters already applied, no
// order/limit of its own). `key` must be unique, non-null, and selected.
// Throws rather than return a partial list.

export async function fetchAllRows(build, { key = "id", pageSize = 1000, maxPages = 500 } = {}) {
  const rows = [];
  let after = null;
  for (let page = 0; page < maxPages; page++) {
    let query = build();
    if (after != null) query = query.gt(key, after);
    const { data, error } = await query.order(key, { ascending: true }).limit(pageSize);
    if (error) throw error;
    if (!Array.isArray(data)) throw new Error("expected an array of rows");
    if (!data.length) return rows;
    const last = data[data.length - 1][key];
    if (last == null || last === after) throw new Error(`paging on "${key}" did not advance`);
    rows.push(...data);
    after = last;
  }
  throw new Error(`more than ${maxPages} pages of ${pageSize} — not treating a partial read as the whole table`);
}

// Default Expenses order: newest created_at first, id as a tiebreak so two
// rows saved in the same instant don't swap places between loads.
export function newestFirst(a, b) {
  const ac = a.created_at || "";
  const bc = b.created_at || "";
  if (ac !== bc) return ac < bc ? 1 : -1;
  const ai = String(a.id || "");
  const bi = String(b.id || "");
  if (ai !== bi) return ai < bi ? 1 : -1;
  return 0;
}

// The Expenses search box. Kept here so a row past the old 1,000 cap is
// tested against the same fields the page matches.
export function invoiceMatchesSearch(inv, raw) {
  const q = String(raw || "").trim().toLowerCase();
  if (!q) return true;
  return (
    inv.supplier?.toLowerCase().includes(q) ||
    inv.invoice_number?.toLowerCase().includes(q) ||
    inv.po_number?.toLowerCase().includes(q) ||
    inv.jobber_job_number?.toLowerCase().includes(q) ||
    inv.client_name?.toLowerCase().includes(q) ||
    inv.admin_notes?.toLowerCase().includes(q) ||
    String(inv.total || "").includes(q)
  );
}
